import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { debounce } from 'lodash'
import { message } from 'antd'

import { 
    searchMedications,
    getMedicationByDispensableDrugId,
    searchSupplies,
    getSupplyById,
 } from '../../../../../api/PrescriptionsApi'

export const useSearchMedications = ({ 
    form,
    selectedTemplate,
    subtype = 'medication',
 }) => {
    const [medicationSearchMatches, setMedicationSearchMatches] = useState([])
    const [medicationSearchLoading, setMedicationSearchLoading] = useState(false)
    const [availableMedicationStrengths, setAvailableMedicationStrengths] = useState([]);

    const abortController = useRef(null)

    const resetMedicationFields = useCallback(() => {
        setMedicationSearchMatches([])
        setAvailableMedicationStrengths([])
        form.setFieldsValue({
            'ndc': null,
            'strength': null,
            'dispensableDrugId': null,
            'supplyId': null,
            'ingredients': null,
            'dispenseUnitId': null,
            'schedule': null,
        })
    }, [form])

    const fetchMedications = useCallback(async (searchStr) => {
        resetMedicationFields()

        if (searchStr?.length <= 2) {
            setMedicationSearchLoading(false);
            return;
        }

        try {
            abortController.current?.abort()
            abortController.current = new AbortController()

            setMedicationSearchLoading(true)
            const { data: { medications } } = await searchMedications(searchStr, abortController.current?.signal)

            if (abortController.current?.signal.aborted || !medications?.length) {
                return
            }

            setMedicationSearchMatches(medications); 
        } catch (e) {
            console.error(e);
            message.error('Error fetching medication matches from prescription service');
        } finally {
            setMedicationSearchLoading(false);
        }
    }, [resetMedicationFields])

    const fetchSupplies = useCallback(async (searchStr) => {
        resetMedicationFields()

        if (searchStr?.length <= 2) {
            setMedicationSearchLoading(false);
            return;
        }

        try {
            abortController.current?.abort()
            abortController.current = new AbortController()

            setMedicationSearchLoading(true)
            const { data: { supplies } } = await searchSupplies({ name: searchStr })

            if (abortController.current?.signal.aborted || !supplies?.length) {
                return
            }

            setMedicationSearchMatches(supplies);
        } catch (e) {
            console.error(e);
            message.error('Error fetching supply matches from prescription service');
        } finally {
            setMedicationSearchLoading(false);
        }
    }, [resetMedicationFields])

    const handleSelectMedication = medicationName => {
        form.setFieldsValue({
            'ndc': null,
            'strength': null,
            'dispensableDrugId': null,
            'supplyId': null,
            'ingredients': null,
            'dispenseUnitId': null,
            'schedule': null,
        })

        setAvailableMedicationStrengths([])

        const medicationMatch = medicationSearchMatches.find(medication => medication.name === medicationName);
        if (!medicationMatch) return;

        const isCompound = medicationMatch.ingredients?.length

        if (isCompound) {            
            form.setFieldsValue({ 
                strength: 'compound', 
                ingredients: medicationMatch.ingredients,
                dispenseUnitId: medicationMatch.dispenseUnitId 
            })

            return setAvailableMedicationStrengths([{ strength: 'compound' }]);
        } 

        setAvailableMedicationStrengths(medicationMatch.strengths);
    };

    const handleSelectSupply = supplyName => {
        const supplyMatch = medicationSearchMatches.find(s => s.name === supplyName);
        if (!supplyMatch) return;

        form.setFieldsValue({
            supplyId: supplyMatch.supplyId?.toString(),
            ndc: supplyMatch.ndc || null,
            dispenseUnitId: supplyMatch.dispenseUnitId || null,
            dispensableDrugId: null,
            strength: null,
            ingredients: null,
            schedule: null,
        })

        setAvailableMedicationStrengths([])
    };

    const handleSelectStrength = strength => {
        const medication = availableMedicationStrengths.find(medStrength => medStrength.strength === strength)

        if (!medication) return;

        form.setFieldsValue({ 
            ndc: medication.ndc,
            dispenseUnitId: medication.dispenseUnitId,
            dispensableDrugId: medication.dispensableDrugId,
            schedule: medication.schedule
        })
    }

    const getTemplateMedicationAndStrength = async dispensableDrugId => {
        try {
            const { data } = await getMedicationByDispensableDrugId(dispensableDrugId)
            return data.medication || null
        } catch (e) {
            console.error(e);
        }
    }

    const getTemplateSupply = async supplyId => {
        try {
            const { data } = await getSupplyById(supplyId)
            return data.supply || null
        } catch (e) {
            console.error(e);
        }
    }

    const searchHandler = useMemo(
        () => subtype === 'supply' ? debounce(fetchSupplies, 1000) : debounce(fetchMedications, 1000),
        [subtype, fetchSupplies, fetchMedications]
    );

    const selectHandler = useMemo(
        () => subtype === 'supply' ? handleSelectSupply : handleSelectMedication,
        [subtype, medicationSearchMatches]
    );

    useEffect(() => {
        if (selectedTemplate?.medication?.dispensableDrugId) {
            form.setFieldsValue({
                dispensableDrugId: selectedTemplate.medication.dispensableDrugId,
            })
        }
        if (selectedTemplate?.medication?.supplyId) {
            form.setFieldsValue({
                supplyId: selectedTemplate.medication.supplyId,
            })
        }
    }, [selectedTemplate])

    return { 
        fetchMedications: searchHandler, 
        medicationSearchMatches, 
        medicationSearchLoading,
        handleSelectMedication: selectHandler,
        handleSelectStrength,
        setMedicationSearchMatches,
        setMedicationSearchLoading,
        availableMedicationStrengths,
        getTemplateMedicationAndStrength,
        getTemplateSupply,
    }
}
